import { CloseIcon } from '@chakra-ui/icons';
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  IconButton,
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useReactFlow } from 'reactflow';

import {
  getGraphNodeById,
  OrgSlugToOrgMapType,
  setSelectedNode,
  TapeBuilderMode,
  useGraph,
} from './GraphContext';
import { SwitchTapePanel } from './SwitchTapePanel';

export const TapeBuilderDrawer = ({
  nodeId,
  isOpen,
  onClose,
  mode,
  orgSlugMap,
  onDelete,
  onConfigChange,
}: {
  isOpen: boolean;
  onClose: () => void;
  nodeId: string;
  mode: TapeBuilderMode;
  orgSlugMap: OrgSlugToOrgMapType;
  onDelete: (nodeId: string) => void;
  onConfigChange: (nodeId: string, config: object) => void;
}) => {
  const graphContext = useGraph();
  const node = getGraphNodeById(graphContext, nodeId);
  const [drawerWidth, setDrawerWidth] = useState(900); // Increased default width for better visibility
  const [isResizing, setIsResizing] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      setIsResizing(true);
      startXRef.current = e.clientX;
      startWidthRef.current = drawerWidth;
      e.preventDefault();
    },
    [drawerWidth],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return;

      const deltaX = startXRef.current - e.clientX;
      const newWidth = Math.max(
        400,
        Math.min(window.innerWidth - 100, startWidthRef.current + deltaX),
      );
      setDrawerWidth(newWidth);
    },
    [isResizing],
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
    return undefined;
  }, [isResizing, handleMouseMove, handleMouseUp]);

  if (!node) {
    return null;
  }

  const nodeDisplayName = node.metadata.tapeBuilderNodeType;

  return (
    <Drawer onClose={onClose} isOpen={isOpen} size="xl" closeOnOverlayClick>
      <DrawerOverlay />
      <DrawerContent maxW={`${drawerWidth}px`} w={`${drawerWidth}px`}>
        {/* Resize handle */}
        <Box
          position="absolute"
          left={0}
          top={0}
          bottom={0}
          width="12px"
          cursor="ew-resize"
          onMouseDown={handleMouseDown}
          bg={isResizing ? 'blue.400' : 'gray.200'}
          _hover={{ bg: 'blue.300' }}
          zIndex={10}
          transition="background-color 0.2s"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {/* Visual indicator - three vertical dots */}
          <Box
            display="flex"
            flexDirection="column"
            gap="3px"
            opacity={0.6}
            _groupHover={{ opacity: 1 }}
          >
            <Box width="3px" height="3px" borderRadius="full" bg="gray.600" />
            <Box width="3px" height="3px" borderRadius="full" bg="gray.600" />
            <Box width="3px" height="3px" borderRadius="full" bg="gray.600" />
          </Box>
        </Box>
        <HStack
          justifyContent="space-between"
          alignItems="start"
          w="100%"
          px="1rem"
          pt="1rem"
        >
          <DrawerHeader flexGrow={1} maxWidth="90%" p={0}>
            {nodeDisplayName}
          </DrawerHeader>
          <IconButton
            aria-label="Close"
            icon={<CloseIcon />}
            onClick={onClose}
            size="sm"
            variant="ghost"
          />
        </HStack>
        <DrawerBody px="1rem" py="1rem" display="flex" flexDirection="column">
          <Box flex={1} display="flex" flexDirection="column" minHeight={0}>
            <SwitchTapePanel
              nodeId={nodeId}
              onDelete={onDelete}
              onConfigChange={onConfigChange}
              mode={mode}
              orgSlugMap={orgSlugMap}
            />
          </Box>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export const TapeBuilderOptionalDrawer = ({
  mode,
  orgSlugMap,
  onDelete,
  onConfigChange,
}: {
  mode: TapeBuilderMode;
  orgSlugMap: OrgSlugToOrgMapType;
  onDelete: (nodeId: string) => void;
  onConfigChange: (nodeId: string, config: object) => void;
}) => {
  const graphContext = useGraph();
  const reactFlowInstance = useReactFlow();
  const selectedNodeId = graphContext.state.selectedNodeId;
  const [isOpen, setIsOpen] = React.useState(false);

  const onClose = () => {
    setIsOpen(false);
    setSelectedNode(graphContext, null);
    // Clear ReactFlow selection state to allow re-selecting the same node
    reactFlowInstance.setNodes((nodes) =>
      nodes.map((node) => ({ ...node, selected: false })),
    );
  };

  React.useEffect(() => {
    setIsOpen(!!selectedNodeId);
  }, [selectedNodeId]);

  if (!selectedNodeId) {
    return null;
  }

  return (
    <TapeBuilderDrawer
      nodeId={selectedNodeId}
      isOpen={isOpen}
      onClose={onClose}
      mode={mode}
      orgSlugMap={orgSlugMap}
      onDelete={onDelete}
      onConfigChange={onConfigChange}
    />
  );
};
